// components/media/Overview.js
import React, { useState, useMemo, useEffect } from 'react';
import {
  Card,
  Typography,
  Badge,
  Button,
  Space,
  Table,
  Statistic,
  Row,
  Col,
  Select,
  Input,
  Modal,
  Image,
  message,
  Dropdown,
  Upload
} from 'antd';
import {
  FileOutlined,
  PictureOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  EditOutlined,
  ClearOutlined,
  CopyOutlined,
  MoreOutlined,
  StarOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteMedia,
  setPagination,
  getFeaturedImage,
  updateFeaturedImage,
  deleteFeaturedImage
} from '../../features/media/mediaSlice';

const { Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const Overview = ({ onEditMedia }) => {
  const dispatch = useDispatch();
  const { 
    media, 
    stats, 
    pagination, 
    filters, 
    loading,
    featuredImages,
    featuredImageUploading,
    processing
  } = useSelector((state) => state.media);

  // Get entity data from other slices
  const { desprojects } = useSelector((state) => state.desprojects);
  const { devprojects } = useSelector((state) => state.devprojects);
  const { blogs } = useSelector((state) => state.blogs);

  const [searchTerm, setSearchTerm] = useState('');
  const [localEntityFilter, setLocalEntityFilter] = useState('all');
  const [localTypeFilter, setLocalTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [featuredImageModal, setFeaturedImageModal] = useState({
    visible: false,
    entityType: null,
    entityId: null,
    entityName: null,
    currentImage: null
  });

  // Get all entities for featured image display
  const getAllEntities = () => {
    const entities = [];
    
    if (desprojects?.length) {
      desprojects.forEach(project => {
        entities.push({
          id: project._id,
          name: project.name,
          type: 'desproject',
          featuredImage: project.filename ? {
            filename: project.filename,
            url: project.url
          } : null
        });
      });
    }
    
    if (devprojects?.length) {
      devprojects.forEach(project => {
        entities.push({
          id: project._id,
          name: project.name,
          type: 'devproject',
          featuredImage: project.filename ? {
            filename: project.filename,
            url: project.url
          } : null
        });
      });
    }
    
    if (blogs?.length) {
      blogs.forEach(blog => {
        entities.push({
          id: blog._id,
          name: blog.title,
          type: 'blog',
          featuredImage: blog.featuredImage || null
        });
      });
    }
    
    return entities;
  };

  // Create combined media list (regular media + featured images)
  const getCombinedMedia = useMemo(() => {
    const combined = [...media];
    
    // Add featured/cover images as pseudo-media items
    getAllEntities().forEach(entity => {
      if (entity.featuredImage) {
        combined.push({
          _id: `featured_${entity.type}_${entity.id}`,
          originalName: `${entity.name} - Featured Image`,
          url: entity.featuredImage.url,
          filename: entity.featuredImage.filename,
          entityType: entity.type,
          entityId: { 
            _id: entity.id,
            name: entity.name,
            title: entity.name
          },
          mediaType: 'image',
          status: 'active',
          fileSize: 0, // Featured images don't track size
          isFeatured: true,
          createdAt: new Date()
        });
      }
    });
    
    return combined;
  }, [media, desprojects, devprojects, blogs]);

  // Filter combined media based on search term and filters
  const filteredMedia = useMemo(() => {
    let filtered = [...getCombinedMedia];

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(item => 
        item.originalName?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.altText?.toLowerCase().includes(searchLower) ||
        item.entityId?.name?.toLowerCase().includes(searchLower) ||
        item.entityId?.title?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by entity type
    if (localEntityFilter !== 'all') {
      filtered = filtered.filter(item => item.entityType === localEntityFilter);
    }

    // Filter by media type
    if (localTypeFilter !== 'all') {
      filtered = filtered.filter(item => item.mediaType === localTypeFilter);
    }

    // Filter out deleted items (only show active items in overview)
    filtered = filtered.filter(item => item.status === 'active');

    return filtered;
  }, [getCombinedMedia, searchTerm, localEntityFilter, localTypeFilter]);

  // Handle search input change
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle entity filter change
  const handleEntityFilterChange = (value) => {
    setLocalEntityFilter(value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle media type filter change
  const handleTypeFilterChange = (value) => {
    setLocalTypeFilter(value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setLocalEntityFilter('all');
    setLocalTypeFilter('all');
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  // Handle pagination change
  const handlePaginationChange = (page, size) => {
    setCurrentPage(page);
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    }
  };

  // Get paginated data for current page
  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredMedia.slice(startIndex, endIndex);
  };

  // Copy URL to clipboard
  const handleCopyUrl = async (url, fileName) => {
    try {
      await navigator.clipboard.writeText(url);
      message.success(`URL copied to clipboard: ${fileName}`);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        message.success(`URL copied to clipboard: ${fileName}`);
      } catch (fallbackErr) {
        message.error('Failed to copy URL to clipboard');
      }
      document.body.removeChild(textArea);
    }
  };

  // Delete media file
  const handleDeleteMedia = async (mediaId, permanent = false) => {
    try {
      await dispatch(deleteMedia({ id: mediaId, permanent })).unwrap();
      message.success('Media deleted successfully');
    } catch (err) {
      message.error('Failed to delete media');
    }
  };

  // Handle featured image operations
  const handleFeaturedImageUpdate = (entityType, entityId, entityName, currentImage) => {
    setFeaturedImageModal({
      visible: true,
      entityType,
      entityId,
      entityName,
      currentImage
    });
  };

  const handleFeaturedImageUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      await dispatch(updateFeaturedImage({
        entityType: featuredImageModal.entityType,
        entityId: featuredImageModal.entityId,
        formData
      })).unwrap();
      message.success('Featured image updated successfully');
      setFeaturedImageModal({ visible: false, entityType: null, entityId: null, entityName: null, currentImage: null });
      onSuccess('ok');
      // Refresh the data - you might want to dispatch a refresh action here
    } catch (err) {
      message.error('Failed to update featured image');
      onError('error');
    }
  };

  const handleFeaturedImageDelete = async (entityType, entityId) => {
    try {
      await dispatch(deleteFeaturedImage({ entityType, entityId })).unwrap();
      message.success('Featured image deleted successfully');
    } catch (err) {
      message.error('Failed to delete featured image');
    }
  };

  // Get actions menu for each record
  const getActionsMenu = (record) => {
    const menuItems = [
      {
        key: 'copy',
        icon: <CopyOutlined />,
        label: 'Copy URL',
        onClick: () => handleCopyUrl(record.url, record.originalName),
      }
    ];

    if (record.isFeatured) {
      // Featured image actions
      menuItems.push(
        {
          key: 'edit-featured',
          icon: <EditOutlined />,
          label: 'Update Featured Image',
          onClick: () => handleFeaturedImageUpdate(
            record.entityType,
            record.entityId._id,
            record.entityId.name || record.entityId.title,
            record
          ),
        },
        {
          type: 'divider',
        },
        {
          key: 'delete-featured',
          icon: <DeleteOutlined />,
          label: 'Remove Featured Image',
          danger: true,
          onClick: () => {
            Modal.confirm({
              title: 'Remove Featured Image',
              content: 'Are you sure you want to remove this featured image?',
              okText: 'Remove',
              okType: 'danger',
              onOk: () => handleFeaturedImageDelete(record.entityType, record.entityId._id)
            });
          },
        }
      );
    } else {
      // Regular media actions
      menuItems.push(
        {
          key: 'edit',
          icon: <EditOutlined />,
          label: 'Edit Details',
          onClick: () => onEditMedia(record),
        },
        {
          type: 'divider',
        },
        {
          key: 'delete',
          icon: <DeleteOutlined />,
          label: 'Delete',
          danger: true,
          onClick: () => {
            Modal.confirm({
              title: 'Delete Media',
              content: 'Are you sure you want to delete this media file?',
              okText: 'Delete',
              okType: 'danger',
              onOk: () => handleDeleteMedia(record._id)
            });
          },
        }
      );
    }

    return {
      items: menuItems,
    };
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getMediaTypeIcon = (mediaType) => {
    switch (mediaType) {
      case 'image':
        return <PictureOutlined />;
      case 'video':
        return <FileOutlined />;
      case 'document':
        return <FileOutlined />;
      default:
        return <FileOutlined />;
    }
  };

  const columns = [
    {
      title: 'Preview',
      dataIndex: 'url',
      key: 'preview',
      width: 100,
      render: (url, record) => (
        <div 
          onClick={() => {
            if (record.mediaType === 'image') {
              Modal.info({
                title: record.originalName,
                content: (
                  <Image
                    width="100%"
                    src={record.url}
                    alt={record.altText}
                  />
                ),
                width: 600,
              });
            } else {
              window.open(record.url, '_blank');
            }
          }}
          style={{ cursor: 'pointer' }}
          title="Click to preview"
        >
          <div style={{ position: 'relative' }}>
            {record.mediaType === 'image' ? (
              <Image
                width={60}
                height={60}
                src={url}
                alt={record.altText}
                style={{ objectFit: 'cover', borderRadius: '6px' }}
                preview={false}
              />
            ) : (
              <div className="w-15 h-15 bg-gray-100 rounded flex items-center justify-center">
                {getMediaTypeIcon(record.mediaType)}
              </div>
            )}
            {record.isFeatured && (
              <StarOutlined 
                style={{ 
                  position: 'absolute', 
                  top: 2, 
                  right: 2, 
                  color: '#faad14',
                  background: 'white',
                  borderRadius: '50%',
                  padding: '2px',
                  fontSize: '12px'
                }} 
                title="Featured Image"
              />
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'File Name',
      dataIndex: 'originalName',
      key: 'originalName',
      ellipsis: true,
      render: (text, record) => (
        <div>
          <Text>{text}</Text>
          {record.isFeatured && (
            <Badge count="Featured" style={{ backgroundColor: '#faad14', marginLeft: 8 }} />
          )}
        </div>
      ),
    },
    {
      title: 'Entity',
      key: 'entity',
      render: (_, record) => (
        <div>
          <Badge color="blue" text={record.entityType} />
          <br />
          <Text type="secondary" className="text-xs">
            {record.entityId?.name || record.entityId?.title || 'Unknown'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'mediaType',
      key: 'mediaType',
      render: (type) => (
        <Badge color="green" text={type} />
      ),
    },
    {
      title: 'Size',
      dataIndex: 'fileSize',
      key: 'fileSize',
      render: (size, record) => record.isFeatured ? 'N/A' : formatFileSize(size),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const color = status === 'active' ? 'green' : status === 'deleted' ? 'red' : 'orange';
        return <Badge color={color} text={status} />;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Dropdown
          menu={getActionsMenu(record)}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            size="small"
            icon={<MoreOutlined />}
            title="More actions"
            className="text-gray-600 hover:text-gray-800 hover:bg-gray-50"
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      {/* Statistics */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} md={8} lg={4} xl={4}>
          <Card>
            <Statistic
              title="Total Media"
              value={stats.totalMedia || 0}
              prefix={<FileOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4} xl={4}>
          <Card>
            <Statistic
              title="Active Media"
              value={stats.activeMedia || 0}
              prefix={<PictureOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4} xl={4}>
          <Card>
            <Statistic
              title="Featured/Cover"
              value={stats.featuredCoverMedia || 0}
              prefix={<StarOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
          <Card>
            <Statistic
              title="Total Size"
              value={stats.totalSizeFormatted || '0 Bytes'}
              prefix={<CloudUploadOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
          <Card>
            <Statistic
              title="Deleted Media"
              value={stats.deletedMedia || 0}
              prefix={<DeleteOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <Select
            placeholder="Filter by entity"
            value={localEntityFilter}
            onChange={handleEntityFilterChange}
            style={{ width: 150 }}
          >
            <Option value="all">All Entities</Option>
            <Option value="desproject">Design Projects</Option>
            <Option value="devproject">Dev Projects</Option>
            <Option value="blog">Blog Posts</Option>
          </Select>

          <Select
            placeholder="Filter by type"
            value={localTypeFilter}
            onChange={handleTypeFilterChange}
            style={{ width: 120 }}
          >
            <Option value="all">All Types</Option>
            <Option value="image">Images</Option>
            <Option value="video">Videos</Option>
            <Option value="document">Documents</Option>
          </Select>

          <div style={{ width: 300 }}>
            <Search
              placeholder="Search by filename, description, or alt text..."
              allowClear
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full"
              size="large"
            />
          </div>

          {(searchTerm || localEntityFilter !== 'all' || localTypeFilter !== 'all') && (
            <Button 
              onClick={handleClearFilters} 
              icon={<ClearOutlined />}
              title="Clear all filters"
            >
              Clear Filters
            </Button>
          )}

          <Text type="secondary" className="text-sm">
            Showing {filteredMedia.length} of {getCombinedMedia.filter(item => item.status === 'active').length} items
            {searchTerm && (
              <span> for "<strong>{searchTerm}</strong>"</span>
            )}
          </Text>
        </div>
      </Card>

      {/* Media Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={getPaginatedData()}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filteredMedia.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} filtered items`,
            onChange: handlePaginationChange,
            onShowSizeChange: handlePaginationChange,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Card>

      {/* Featured Image Update Modal */}
      <Modal
        title={`Update Featured Image - ${featuredImageModal.entityName}`}
        visible={featuredImageModal.visible}
        onCancel={() => setFeaturedImageModal({ visible: false, entityType: null, entityId: null, entityName: null, currentImage: null })}
        footer={null}
        width={600}
      >
        <div className="mb-4">
          {featuredImageModal.currentImage && (
            <div className="mb-4">
              <Text strong>Current Image:</Text>
              <div className="mt-2">
                <Image
                  width={200}
                  src={featuredImageModal.currentImage.url}
                  alt="Current featured image"
                  style={{ borderRadius: '8px' }}
                />
              </div>
            </div>
          )}
          
          <Upload.Dragger
            customRequest={handleFeaturedImageUpload}
            showUploadList={false}
            accept="image/*"
            loading={featuredImageUploading}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">Click or drag image to upload</p>
            <p className="ant-upload-hint">
              Upload a new featured image for this {featuredImageModal.entityType === 'blog' ? 'blog post' : 'project'}
            </p>
          </Upload.Dragger>
        </div>
      </Modal>
    </div>
  );
};

export default Overview;